import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { FilterInput, FilterType } from 'generated/graphql';
import useNumberParams from './useNumberParams';

/**
 * Reports dashboard URL query params: productID and product filter params (cat, var, cla, siz, wei).
 * Exposes values, productFilterInputs derived from them, and removeProductIDQueryParam to clear them.
 */

interface ReportsDashboardURLParamsContextProps {
  productID: number;
  catURLParam: number | undefined;
  varURLParam: number | undefined;
  claURLParam: number | undefined;
  sizURLParam: number | undefined;
  weiURLParam: number | undefined;
  productFilterInputs: FilterInput[];
  removeProductIDQueryParam: () => void;
}

const Context = createContext<ReportsDashboardURLParamsContextProps | undefined>(undefined);

export const ReportsDashboardURLParamsProvider = ({ children }: { children: ReactNode }) => {
  const [getNumberParam] = useNumberParams();
  const navigate = useNavigate();
  const location = useLocation();

  const productID = getNumberParam('productID') ?? 0;
  const catURLParam = getNumberParam('cat');
  const varURLParam = getNumberParam('var');
  const claURLParam = getNumberParam('cla');
  const sizURLParam = getNumberParam('siz');
  const weiURLParam = getNumberParam('wei');

  const removeProductIDQueryParam = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('productID');
    searchParams.delete('cat');
    searchParams.delete('var');
    searchParams.delete('cla');
    searchParams.delete('wei');
    searchParams.delete('siz');
    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      { replace: true },
    );
  }, [navigate, location.pathname, location.search]);

  const productFilterInputs: FilterInput[] = useMemo(
    () => [
      { type: FilterType.Category, selectedIDs: [String(catURLParam ?? 0)] },
      { type: FilterType.Variety, selectedIDs: [String(varURLParam ?? 0)] },
      { type: FilterType.Class, selectedIDs: [String(claURLParam ?? 0)] },
      { type: FilterType.Size, selectedIDs: [String(sizURLParam ?? 0)] },
      { type: FilterType.Weight, selectedIDs: [String(weiURLParam ?? 0)] },
    ],
    [catURLParam, varURLParam, claURLParam, sizURLParam, weiURLParam],
  );

  const value = useMemo(
    () => ({
      productID,
      catURLParam,
      varURLParam,
      claURLParam,
      sizURLParam,
      weiURLParam,
      productFilterInputs,
      removeProductIDQueryParam,
    }),
    [
      productID,
      catURLParam,
      varURLParam,
      claURLParam,
      sizURLParam,
      weiURLParam,
      productFilterInputs,
      removeProductIDQueryParam,
    ],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useReportsDashboardURLParams = () => {
  const result = useContext(Context);
  if (!result) {
    throw new Error('useReportsDashboardURLParams must be used within a ReportsDashboardURLParamsProvider');
  }
  return result;
};
